import React from 'react';
// Import Ant Design components
import { Button, Table } from 'antd';
import type { TableProps, ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

export type UsageEventProps = {
  events: IUsageEvent[] | undefined;
  sortBy: 'event_type' | 'title' | 'status' | 'created_at' | 'usage_time';
  sortDirection: 'descend' | 'ascend';
  handleSort: (sortBy: 'event_type' | 'title' | 'status' | 'created_at' | 'usage_time') => void;
  onClickDetails: (eventId: string) => void;
  page: { current: number; perPage: number };
  handlePage: (page: { current: number; perPage: number }) => void;
};

export interface IUsageEvent {
  created_at: string;
  event_type: string;
  id: number;
  job_id: string;
  logged_at: string;
  title: string;
  status: string;
  plan: string;
  metadata: Record<string, string>;
  usage_time: number;
  usage: Usage;
  user_id: string;
}

export type Usage = Record<string, number>;

export default function UsageEvent(props: UsageEventProps) {
  const { events, sortBy, sortDirection, handleSort, onClickDetails, page, handlePage } = props;

  const handleTableChange: TableProps<IUsageEvent>['onChange'] = (pagination, filters, sorter) => {
    // Check if sorting is what triggered the change
    if (!Array.isArray(sorter) && sorter.columnKey) {
      const sortKey = sorter.columnKey as UsageEventProps['sortBy'];
      handleSort(sortKey);
    }
    if (pagination.current) {
      handlePage({
        current: pagination.current,
        perPage: pagination.pageSize ?? 10,
      });
    }
  };

  const columns: ColumnsType<IUsageEvent> = [
    {
      title: 'Event Type',
      dataIndex: 'event_type',
      key: 'event_type',
      sorter: true,
      sortOrder: sortBy === 'event_type' ? sortDirection : undefined,
    },
    {
      title: 'Title',
      dataIndex: 'title',
      key: 'title',
      sorter: true,
      sortOrder: sortBy === 'title' ? sortDirection : undefined,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortOrder: sortBy === 'status' ? sortDirection : undefined,
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortOrder: sortBy === 'created_at' ? sortDirection : undefined,
      render: (value: string) => dayjs(value).format('L LT'),
    },
    {
      title: 'Usage Time',
      dataIndex: 'usage_time',
      key: 'usage_time',
      sorter: true,
      sortOrder: sortBy === 'usage_time' ? sortDirection : undefined,
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => <Button onClick={() => onClickDetails(record.job_id)}>Details</Button>,
    },
  ];

  return (
    <Table
      style={{
        minWidth: '856px',
      }}
      columns={columns}
      dataSource={events}
      rowKey='id'
      scroll={{ x: true }}
      onChange={handleTableChange}
      loading={events === undefined}
      pagination={{
        current: page.current,
        pageSize: page.perPage,
      }}
    />
  );
}
